import { Platform } from 'react-native';
import { submitFaceVerifiedAttendance } from './faceVerificationService';
import { apiClient } from '../api/apiClient';
import { normalizeApiError } from '../api/apiError';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  AdminAttendanceDashboardData,
  AdminAttendanceDashboardMetrics,
  AttendanceDetails,
  AttendanceEvent,
  AttendanceHistoryFilters,
  AttendanceHistoryItem,
  AttendanceHistoryResult,
  HrAdminCompensatoryWorkItem,
  HrAdminDashboardData,
  HrAdminLeaveItem,
  HrAdminPermissionItem,
  HrAdminWfhItem,
} from '../types/attendance';
import type {
  WeeklyAttendanceDay,
  WeeklyAttendanceEmployeeReport,
  WeeklyAttendanceResult,
  WeeklyAttendanceSummary,
} from '../types/weeklyAttendanceReport';
import type {
  EmployeeMonthlyAttendanceDay,
  EmployeeMonthlyAttendanceLegendEntry,
  EmployeeMonthlyAttendanceResult,
  EmployeeMonthlyAttendanceRow,
} from '../types/employeeMonthlyAttendanceReport';
import {
  createDefaultAttendanceFilters,
  normalizeAttendanceFilters,
} from '../utils/attendanceFilters';
import {
  getAttendanceStatusLabel,
  getAttendanceStatusReason,
} from '../utils/attendanceStatus';
import {
  breakStateFromEvents,
  clockOnDateToIso,
  parseDurationToMinutes,
} from '../utils/checkoutBreak';
import type { DashboardScope } from '../types/dashboard';
import { calendarDateFromApi } from '../utils/dateTime';
import { punchSourceFromApiRow } from '../utils/attendanceEventSource';
import { applyWorkHoursCoverageToWeeklyDay } from '../utils/weeklyAttendanceReport';
import {
  checkedOutTotalWorkMinutes,
  isAttendanceCheckedOut,
  resolveAttendanceSpreadMinutes,
} from '../utils/totalHours';
import type { HolidayRecord } from '../types/officeCalendar';
import type { LateArrivalDeduction } from '../types/lateArrivalConfiguration';
import { holidayDateFromApi } from '../utils/officeCalendar';
import { parseAttendanceDayStatus } from '../utils/attendanceDayStatus';
import { isHrDashboardRequestStatus } from '../utils/workflowStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function dateOnly(value: unknown): string | null {
  return calendarDateFromApi(value);
}

function validateAttendanceDate(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw Object.assign(
      new Error('attendanceDate must use YYYY-MM-DD format'),
      {
        code: 'VALIDATION_ERROR',
      },
    );
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value
  ) {
    throw Object.assign(new Error('attendanceDate is invalid'), {
      code: 'VALIDATION_ERROR',
    });
  }
  return value;
}

const EVENT_LABELS: Record<string, string> = {
  CHECK_IN: 'Check-in',
  CHECK_OUT: 'Check-out',
  BREAK_OUT: 'Break started',
  BREAK_IN: 'Break ended',
};

function eventLabel(eventType: string | null): string {
  if (!eventType) return 'Attendance event';
  return EVENT_LABELS[eventType] ?? 'Attendance event';
}

function minutesFrom(row: ObjectValue, keys: string[]): number | null {
  for (const key of keys) {
    const value = numberOrNull(row[key]);
    if (value !== null) return value;
  }
  return null;
}

function toSnakeKey(key: string): string {
  return key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

function durationMinutesOrNull(value: unknown): number | null {
  const numeric = numberOrNull(value);
  if (numeric !== null) return numeric;
  if (typeof value !== 'string' || !value.trim()) return null;
  const clock = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (clock) {
    return Number(clock[1]) * 60 + Number(clock[2]);
  }
  return parseDurationToMinutes(value);
}

function firstDurationMinutes(row: ObjectValue, keys: string[]): number | null {
  let zero: number | null = null;
  for (const key of keys) {
    for (const candidate of [key, toSnakeKey(key)]) {
      const minutes = durationMinutesOrNull(row[candidate]);
      if (minutes === null) continue;
      if (minutes > 0) return minutes;
      zero = 0;
    }
  }
  return zero;
}

function historyEvents(row: ObjectValue): Array<{
  eventType: string | null;
  localTimestamp: string | null;
  timestamp: string | null;
}> {
  const raw = Array.isArray(row.events) ? row.events : [];
  return raw.map(item => {
    const event = (item ?? {}) as ObjectValue;
    return {
      eventType: textOrNull(event.eventType ?? event.event_type),
      localTimestamp: textOrNull(
        event.eventLocalTimestamp ??
          event.localTimestamp ??
          event.event_local_timestamp,
      ),
      timestamp: textOrNull(
        event.eventTimestampUtc ??
          event.eventTimestamp ??
          event.event_timestamp_utc,
      ),
    };
  });
}

function flattenHistoryRow(row: ObjectValue): ObjectValue {
  const nested =
    row.attendance && typeof row.attendance === 'object'
      ? (row.attendance as ObjectValue)
      : null;
  return nested ? { ...row, ...nested } : row;
}

function normalizeHistoryHoliday(row: ObjectValue): HolidayRecord | null {
  const holidayDate = holidayDateFromApi(row.holidayDate);
  const holidayName = textOrNull(row.holidayName);
  if (!holidayDate || !holidayName) return null;
  const holidayType = textOrNull(row.holidayType) ?? 'OTHER';
  return {
    holidayId: numberOrNull(row.holidayId) ?? 0,
    holidayDate,
    holidayName,
    holidayType: holidayType as HolidayRecord['holidayType'],
    description: textOrNull(row.description),
  };
}

function normalizeHistoryItem(row: ObjectValue): AttendanceHistoryItem | null {
  const source = flattenHistoryRow(row);
  const attendanceDate = dateOnly(
    source.attendanceDate ?? source.attendance_date,
  );
  if (!attendanceDate) return null;

  const statusCode =
    textOrNull(source.statusCode) ??
    textOrNull(source.attendanceStatusCode) ??
    textOrNull(source.attendance_status_code) ??
    textOrNull(source.status);
  const statusName =
    textOrNull(source.statusLabel) ??
    textOrNull(source.statusName) ??
    textOrNull(source.attendanceStatusName) ??
    textOrNull(source.attendance_status_name);
  const lateMinutes = minutesFrom(source, ['lateMinutes', 'late_minutes']);
  const mappedLabel = getAttendanceStatusLabel(statusCode ?? statusName);
  const dayStatus = parseAttendanceDayStatus(source.dayStatus);
  const persistedBreakMinutes = firstDurationMinutes(source, [
    'breakMinutes',
    'lunchBreakMinutes',
    'totalBreakMinutes',
  ]);
  const eventBreakMinutes =
    breakStateFromEvents(historyEvents(row)).completedBreakMinutes;
  const breakMinutes =
    persistedBreakMinutes != null && persistedBreakMinutes > 0
      ? persistedBreakMinutes
      : (eventBreakMinutes ?? persistedBreakMinutes);

  return {
    attendanceId: numberOrNull(
      source.dailyAttendanceId ??
        source.daily_attendance_id ??
        source.attendanceId,
    ),
    attendanceDate,
    statusCode: dayStatus?.code ?? statusCode,
    statusLabel: dayStatus
      ? dayStatus.label
      : mappedLabel !== 'Status unavailable'
        ? mappedLabel
        : statusName ?? mappedLabel,
    statusReason: dayStatus ? null : getAttendanceStatusReason({ status: statusCode }),
    statusNotes: dayStatus?.notes ?? [],
    dayStatus,
    shiftName: textOrNull(source.shiftName ?? source.shift_name),
    checkInTime: textOrNull(
      source.firstCheckInAt ??
        source.first_check_in_at ??
        source.firstCheckInLocal ??
        source.checkInTime,
    ),
    checkOutTime: textOrNull(
      source.lastCheckOutAt ??
        source.last_check_out_at ??
        source.lastCheckOutLocal ??
        source.checkOutTime,
    ),
    workedMinutes: minutesFrom(source, [
      'netWorkMinutes',
      'net_work_minutes',
      'workedMinutes',
      'grossWorkMinutes',
      'gross_work_minutes',
      'totalWorkedMinutes',
    ]),
    breakMinutes,
    lateMinutes,
    earlyDepartureMinutes: minutesFrom(source, [
      'earlyExitMinutes',
      'early_exit_minutes',
      'earlyDepartureMinutes',
    ]),
    overtimeMinutes: minutesFrom(source, [
      'overtimeMinutes',
      'overtime_minutes',
    ]),
    expectedWorkMinutes: minutesFrom(source, [
      'expectedWorkMinutes',
      'expected_work_minutes',
    ]),
    spreadMinutes: minutesFrom(source, ['spreadMinutes', 'spread_minutes']),
    isCheckedOut: boolOrNull(source.isCheckedOut ?? source.is_checked_out),
    totalWorkMinutes: minutesFrom(source, [
      'totalWorkMinutes',
      'total_work_minutes',
    ]),
    attendanceMode: textOrNull(
      source.attendanceMode ??
        source.attendance_mode ??
        source.attendanceSource,
    ),
    isRegularized: boolOrNull(
      source.isManuallyAdjusted ??
        source.is_manually_adjusted ??
        source.isRegularized,
    ),
  };
}

function normalizeEvent(row: ObjectValue): AttendanceEvent {
  const eventType = textOrNull(row.eventType ?? row.event_type);
  const punchSource = punchSourceFromApiRow(row);
  return {
    eventId: numberOrNull(
      row.attendanceEventId ?? row.attendance_event_id ?? row.eventId,
    ),
    eventType,
    eventLabel: eventLabel(eventType),
    timestamp: textOrNull(
      row.eventTimestampUtc ??
        row.event_timestamp_utc ??
        row.eventTimestamp ??
        row.event_timestamp,
    ),
    localTimestamp: textOrNull(
      row.eventLocalTimestamp ??
        row.event_local_timestamp ??
        row.localTimestamp ??
        row.local_timestamp,
    ),
    timezone: textOrNull(row.eventTimezone ?? row.event_timezone ?? row.timezone),
    locationName: textOrNull(row.locationName ?? row.location_name),
    source: punchSource.source ?? textOrNull(row.attendanceMode),
    originalSource: punchSource.originalSource,
    isManuallyEdited: punchSource.isManuallyEdited,
    editedByName: punchSource.editedByName,
    editedAt: punchSource.editedAt,
    editReason: punchSource.editReason,
    deviceIdentifier: textOrNull(
      row.deviceIdentifier ?? row.device_identifier ?? row.deviceName,
    ),
    kioskId:
      punchSource.kioskId ??
      numberOrNull(row.kioskId ?? row.kiosk_id),
    faceVerified: boolOrNull(
      row.faceVerified ??
        row.face_verified ??
        (numberOrNull(
          row.faceVerificationLogId ?? row.face_verification_log_id,
        ) != null
          ? true
          : undefined),
    ),
  };
}

function eventsFromDetailsPayload(
  data: ObjectValue,
  attendance: ObjectValue,
): ObjectValue[] {
  const top = Array.isArray(data.events) ? data.events : [];
  const nested = Array.isArray(attendance.events) ? attendance.events : [];
  return top.length > 0 ? top : nested;
}

function sortEvents(events: AttendanceEvent[]): AttendanceEvent[] {
  return [...events].sort((left, right) => {
    const leftKey = left.localTimestamp ?? left.timestamp ?? '';
    const rightKey = right.localTimestamp ?? right.timestamp ?? '';
    if (leftKey === rightKey) {
      return (left.eventId ?? 0) - (right.eventId ?? 0);
    }
    return leftKey < rightKey ? -1 : 1;
  });
}

function pickEvent(
  events: AttendanceEvent[],
  type: string,
): AttendanceEvent | undefined {
  if (type === 'CHECK_IN') {
    return events.find(event => event.eventType === 'CHECK_IN');
  }
  const matches = events.filter(event => event.eventType === type);
  return matches[matches.length - 1];
}

function normalizeLateArrivalDeduction(
  row: ObjectValue | null | undefined,
): LateArrivalDeduction | null {
  if (!row) return null;
  return {
    lateArrivalDeductionId: numberOrNull(row.lateArrivalDeductionId),
    attendanceDate: dateOnly(row.attendanceDate),
    occurrenceNumber: numberOrNull(row.occurrenceNumber) ?? 0,
    lateMinutes: numberOrNull(row.lateMinutes) ?? 0,
    thresholdMinutes: numberOrNull(row.thresholdMinutes) ?? 0,
    leaveTypeId: numberOrNull(row.leaveTypeId),
    leaveTypeName: textOrNull(row.leaveTypeName),
    ruleDeductionDays: numberOrNull(row.ruleDeductionDays) ?? 0,
    leaveDeductedDays: numberOrNull(row.leaveDeductedDays) ?? 0,
    lopDays: numberOrNull(row.lopDays) ?? 0,
  };
}

function normalizeDetails(
  attendance: ObjectValue,
  events: ObjectValue[],
  lateArrivalDeduction?: ObjectValue | null,
): AttendanceDetails {
  const attendanceDate = dateOnly(attendance.attendanceDate);
  if (!attendanceDate) {
    throw Object.assign(new Error('Attendance date is missing'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const statusCode =
    textOrNull(attendance.statusCode) ??
    textOrNull(attendance.attendanceStatusCode) ??
    textOrNull(attendance.status);
  const statusName =
    textOrNull(attendance.statusLabel) ??
    textOrNull(attendance.statusName) ??
    textOrNull(attendance.attendanceStatusName);
  const lateMinutes = minutesFrom(attendance, ['lateMinutes']);
  const mappedLabel = getAttendanceStatusLabel(statusCode ?? statusName);
  const dayStatus = parseAttendanceDayStatus(attendance.dayStatus);
  const normalizedEvents = sortEvents(events.map(normalizeEvent));
  const checkInEvent = pickEvent(normalizedEvents, 'CHECK_IN');
  const checkOutEvent = pickEvent(normalizedEvents, 'CHECK_OUT');
  const isCheckedOut = isAttendanceCheckedOut({
    isCheckedOut: boolOrNull(attendance.isCheckedOut),
    checkOutTime: textOrNull(
      checkOutEvent?.timestamp ??
        attendance.lastCheckOutAt ??
        attendance.checkOutTime,
    ),
  });
  const faceAttendanceVerified = normalizedEvents.some(
    event => event.faceVerified === true,
  )
    ? true
    : normalizedEvents.some(event => event.faceVerified === false)
    ? false
    : null;

  return {
    attendanceId: numberOrNull(
      attendance.dailyAttendanceId ?? attendance.attendanceId,
    ),
    attendanceDate,
    statusCode: dayStatus?.code ?? statusCode,
    statusLabel: dayStatus
      ? dayStatus.label
      : mappedLabel !== 'Status unavailable'
        ? mappedLabel
        : statusName ?? mappedLabel,
    statusReason: dayStatus ? null : getAttendanceStatusReason({ status: statusCode }),
    statusNotes: dayStatus?.notes ?? [],
    dayStatus,
    attendanceMode: textOrNull(
      attendance.attendanceMode ?? attendance.attendanceSource,
    ),
    shift: {
      shiftId: numberOrNull(attendance.shiftId),
      shiftName: textOrNull(attendance.shiftName),
      scheduledStartTime: textOrNull(
        attendance.scheduledStartAt ?? attendance.scheduledStartTime,
      ),
      scheduledEndTime: textOrNull(
        attendance.scheduledEndAt ?? attendance.scheduledEndTime,
      ),
    },
    checkIn: {
      timestamp: textOrNull(
        checkInEvent?.timestamp ??
          attendance.firstCheckInAt ??
          attendance.checkInTime,
      ),
      localTimestamp: textOrNull(checkInEvent?.localTimestamp),
      locationName: textOrNull(
        checkInEvent?.locationName ?? attendance.checkInLocationName,
      ),
      deviceName: textOrNull(
        checkInEvent?.deviceIdentifier ?? attendance.checkInDeviceIdentifier,
      ),
      source: textOrNull(checkInEvent?.source ?? attendance.attendanceMode),
      originalSource: checkInEvent?.originalSource ?? null,
      isManuallyEdited: checkInEvent?.isManuallyEdited ?? null,
      editedByName: checkInEvent?.editedByName ?? null,
      editedAt: checkInEvent?.editedAt ?? null,
      editReason: checkInEvent?.editReason ?? null,
      faceVerified: checkInEvent?.faceVerified ?? null,
      deviceIdentifier: checkInEvent?.deviceIdentifier ?? null,
      kioskId: checkInEvent?.kioskId ?? null,
    },
    checkOut: {
      timestamp: textOrNull(
        checkOutEvent?.timestamp ??
          attendance.lastCheckOutAt ??
          attendance.checkOutTime,
      ),
      localTimestamp: textOrNull(checkOutEvent?.localTimestamp),
      locationName: textOrNull(
        checkOutEvent?.locationName ?? attendance.checkOutLocationName,
      ),
      deviceName: textOrNull(
        checkOutEvent?.deviceIdentifier ?? attendance.checkOutDeviceIdentifier,
      ),
      source: textOrNull(checkOutEvent?.source ?? attendance.attendanceMode),
      originalSource: checkOutEvent?.originalSource ?? null,
      isManuallyEdited: checkOutEvent?.isManuallyEdited ?? null,
      editedByName: checkOutEvent?.editedByName ?? null,
      editedAt: checkOutEvent?.editedAt ?? null,
      editReason: checkOutEvent?.editReason ?? null,
      faceVerified: checkOutEvent?.faceVerified ?? null,
      deviceIdentifier: checkOutEvent?.deviceIdentifier ?? null,
      kioskId: checkOutEvent?.kioskId ?? null,
    },
    durations: (() => {
      const worked = minutesFrom(attendance, [
        'netWorkMinutes',
        'workedMinutes',
        'grossWorkMinutes',
        'totalWorkedMinutes',
      ]);
      const persistedBreak = firstDurationMinutes(attendance, [
        'breakMinutes',
        'lunchBreakMinutes',
        'totalBreakMinutes',
      ]);
      const eventBreak =
        breakStateFromEvents(normalizedEvents).completedBreakMinutes;
      // Prefer stored totals when recalculated; otherwise derive from event pairs
      // so break duration shows immediately after end-break (before checkout).
      const breakMins =
        persistedBreak != null && persistedBreak > 0
          ? persistedBreak
          : (eventBreak ?? persistedBreak);
      const gross = minutesFrom(attendance, [
        'grossWorkMinutes',
        'totalWorkMinutes',
        'total_work_minutes',
      ]);
      return {
        workedMinutes: worked,
        breakMinutes: breakMins,
        lateMinutes,
        earlyDepartureMinutes: minutesFrom(attendance, [
          'earlyExitMinutes',
          'earlyDepartureMinutes',
        ]),
        overtimeMinutes: minutesFrom(attendance, ['overtimeMinutes']),
        expectedWorkMinutes: minutesFrom(attendance, [
          'expectedWorkMinutes',
          'expected_work_minutes',
        ]),
        spreadMinutes: minutesFrom(attendance, [
          'spreadMinutes',
          'spread_minutes',
        ]),
        totalHoursMinutes: checkedOutTotalWorkMinutes({
          isCheckedOut,
          workedMinutes: minutesFrom(attendance, [
            'netWorkMinutes',
            'workedMinutes',
          ]),
          breakMinutes: breakMins,
          grossWorkMinutes: gross,
        }),
      };
    })(),
    isCheckedOut,
    events: normalizedEvents,
    faceAttendanceVerified,
    lateArrivalDeduction: normalizeLateArrivalDeduction(lateArrivalDeduction),
  };
}

export async function getAttendanceHistory(
  options: {
    filters?: Partial<AttendanceHistoryFilters>;
    page?: number;
    pageSize?: number;
    /** Optional employee scope for admins viewing another employee's history. */
    employeeId?: number;
  } = {},
): Promise<AttendanceHistoryResult> {
  const defaults = createDefaultAttendanceFilters();
  const normalized = normalizeAttendanceFilters({
    fromDate: options.filters?.fromDate ?? defaults.fromDate,
    toDate: options.filters?.toDate ?? defaults.toDate,
    attendanceMode: options.filters?.attendanceMode,
    page: options.page ?? 1,
    pageSize: options.pageSize ?? 20,
  });

  const params: Record<string, string | number> = {
    fromDate: normalized.filters.fromDate,
    toDate: normalized.filters.toDate,
    page: normalized.page,
    pageSize: normalized.pageSize,
  };
  if (normalized.filters.attendanceMode) {
    params.attendanceMode = normalized.filters.attendanceMode;
  }
  if (
    typeof options.employeeId === 'number' &&
    Number.isInteger(options.employeeId) &&
    options.employeeId > 0
  ) {
    params.employeeId = options.employeeId;
  }

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      holidays?: ObjectValue[];
      days?: ObjectValue[];
      pagination?: {
        page?: unknown;
        pageSize?: unknown;
        totalRecords?: unknown;
        totalPages?: unknown;
      };
    }>
  >(API_ENDPOINTS.ATTENDANCE.HISTORY, { params });

  const data = response.data.data ?? {};
  const pagination = data.pagination ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeHistoryItem)
        .filter((item): item is AttendanceHistoryItem => item !== null)
    : [];
  const holidays = Array.isArray(data.holidays)
    ? data.holidays
        .map(normalizeHistoryHoliday)
        .filter((holiday): holiday is HolidayRecord => holiday !== null)
    : [];
  const days = Array.isArray(data.days)
    ? data.days
        .map(normalizeHistoryItem)
        .filter((item): item is AttendanceHistoryItem => item !== null)
    : [];
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const pageSize = numberOrNull(pagination.pageSize) ?? normalized.pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / Math.max(pageSize, 1)));

  return {
    items,
    holidays,
    days,
    page: numberOrNull(pagination.page) ?? normalized.page,
    pageSize,
    totalItems,
    totalPages,
  };
}

export async function getAttendanceDetails(
  attendanceDate: string,
  options: { employeeId?: number } = {},
): Promise<AttendanceDetails | null> {
  const safeDate = validateAttendanceDate(attendanceDate.trim());
  const params: Record<string, number> = {};
  if (
    typeof options.employeeId === 'number' &&
    Number.isInteger(options.employeeId) &&
    options.employeeId > 0
  ) {
    params.employeeId = options.employeeId;
  }
  let response;
  try {
    response =
      Object.keys(params).length > 0
        ? await apiClient.get<
            Envelope<{ attendance?: ObjectValue | null; events?: ObjectValue[] }>
          >(API_ENDPOINTS.ATTENDANCE.DETAILS(safeDate), { params })
        : await apiClient.get<
            Envelope<{ attendance?: ObjectValue | null; events?: ObjectValue[] }>
          >(API_ENDPOINTS.ATTENDANCE.DETAILS(safeDate));
  } catch (error) {
    // Older backends answer an empty day with 404 ATTENDANCE_NOT_FOUND.
    if (normalizeApiError(error).code === 'ATTENDANCE_NOT_FOUND') return null;
    throw error;
  }

  const data = response.data.data ?? {};
  // No attendance row for this employee/date is a valid empty day.
  const attendance =
    data.attendance && typeof data.attendance === 'object'
      ? (data.attendance as ObjectValue)
      : null;
  if (!attendance || Object.keys(attendance).length === 0) return null;
  const events = eventsFromDetailsPayload(data, attendance);
  return normalizeDetails(
    attendance,
    events,
    (data as { lateArrivalDeduction?: ObjectValue | null }).lateArrivalDeduction,
  );
}

export type AttendancePunchOptions = {
  eventTimestamp?: string;
  attendanceSource?: string;
  employeeId?: number;
  latitude?: number;
  longitude?: number;
  gpsAccuracyMeters?: number | null;
  faceVerificationLogId?: number;
  kioskId?: number;
  deviceIdentifier?: string;
  livenessSessionId?: number | null;
  /** Optional clocks from mobile check-out. Omit both when no break was taken. */
  breakStartTime?: string | null;
  breakEndTime?: string | null;
};

function punchBody(
  options: AttendancePunchOptions | undefined,
  defaultSource: string,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    eventTimestamp: options?.eventTimestamp ?? new Date().toISOString(),
    attendanceSource: options?.attendanceSource ?? defaultSource,
  };
  if (
    typeof options?.employeeId === 'number' &&
    Number.isInteger(options.employeeId) &&
    options.employeeId > 0
  ) {
    body.employeeId = options.employeeId;
  }
  if (
    typeof options?.faceVerificationLogId === 'number' &&
    Number.isInteger(options.faceVerificationLogId) &&
    options.faceVerificationLogId > 0
  ) {
    body.faceVerificationLogId = options.faceVerificationLogId;
  }
  if (
    typeof options?.kioskId === 'number' &&
    Number.isInteger(options.kioskId) &&
    options.kioskId > 0
  ) {
    body.kioskId = options.kioskId;
  }
  if (options?.deviceIdentifier?.trim()) {
    body.deviceIdentifier = options.deviceIdentifier.trim().slice(0, 255);
  }
  if (
    typeof options?.livenessSessionId === 'number' &&
    Number.isInteger(options.livenessSessionId) &&
    options.livenessSessionId > 0
  ) {
    body.livenessSessionId = options.livenessSessionId;
  }
  const breakStartTime = options?.breakStartTime?.trim();
  const breakEndTime = options?.breakEndTime?.trim();
  if (breakStartTime && breakEndTime) {
    body.breakStartTime = breakStartTime;
    body.breakEndTime = breakEndTime;
  }
  if (
    typeof options?.latitude === 'number' &&
    Number.isFinite(options.latitude) &&
    typeof options?.longitude === 'number' &&
    Number.isFinite(options.longitude)
  ) {
    body.latitude = options.latitude;
    body.longitude = options.longitude;
    if (
      typeof options.gpsAccuracyMeters === 'number' &&
      Number.isFinite(options.gpsAccuracyMeters)
    ) {
      body.gpsAccuracyMeters = options.gpsAccuracyMeters;
    }
  }
  return body;
}

/**
 * All four self-service actions capture a face through the shared verification
 * service. The API verifies that capture before executing attendance rules.
 * Authenticated kiosk continuations retain their existing proof contract.
 */
const WFH_CHECK_IN_BLOCK_CODES = new Set([
  'WFH_CHECK_IN_PENDING',
  'WFH_CHECK_IN_REJECTED',
  'WFH_CHECK_IN_NOT_APPROVED',
]);

function isWfhPunchSource(source: unknown): boolean {
  const code = typeof source === 'string' ? source.trim().toUpperCase() : '';
  return code === 'WFH' || code === 'WFH_APP';
}

/**
 * Asks the API whether today's WFH request is approved. A transport or
 * unexpected failure returns null so check-in itself can still enforce the rule.
 */
export async function getWfhCheckInBlockMessage(): Promise<string | null> {
  try {
    await apiClient.get(API_ENDPOINTS.ATTENDANCE.WFH_CHECK_IN);
    return null;
  } catch (error) {
    const apiError = normalizeApiError(error);
    if (apiError.code && WFH_CHECK_IN_BLOCK_CODES.has(apiError.code)) {
      return apiError.message;
    }
    return null;
  }
}

export type AttendanceActionResult = {
  eventTime: string | null;
  localEventTime: string | null;
  checkInTime: string | null;
  breakMinutes: number | null;
  workedMinutes: number | null;
};

function asRecord(value: unknown): ObjectValue {
  return value && typeof value === 'object' ? (value as ObjectValue) : {};
}

/** Accept either the action row or an axios envelope from tests and callers. */
function unwrapActionPayload(value: unknown): ObjectValue {
  const record = asRecord(value);
  const envelope = asRecord(record.data);
  if (envelope.data && typeof envelope.data === 'object') {
    return asRecord(envelope.data);
  }
  return record;
}

function actionText(row: ObjectValue, keys: string[]): string | null {
  for (const key of keys) {
    const value = textOrNull(row[key] ?? row[toSnakeKey(key)]);
    if (value) return value;
  }
  return null;
}

export function attendanceActionResult(payload: unknown): AttendanceActionResult {
  const row = unwrapActionPayload(payload);
  return {
    eventTime: actionText(row, ['eventTimestampUtc', 'eventTimestamp', 'eventTime']),
    localEventTime: actionText(row, ['eventLocalTimestamp', 'localTimestamp']),
    checkInTime: actionText(row, ['firstCheckInAt', 'checkInTime']),
    breakMinutes: firstDurationMinutes(row, [
      'breakMinutes',
      'totalBreakMinutes',
      'lunchBreakMinutes',
    ]),
    workedMinutes: minutesFrom(row, [
      'netWorkMinutes',
      'workedMinutes',
      'netWorkedMinutes',
      'totalWorkedMinutes',
    ]),
  };
}

/**
 * Self-service punches (check-in, break, check-out) use the app channel when
 * no work-location code is supplied. Native mobile → MOBILE_APP; web → WEB_PORTAL.
 * Kiosk punches set OFFICE_KIOSK / WEB_PORTAL themselves and are not covered here.
 */
export function defaultSelfServicePunchSource(): string {
  return Platform.OS === 'web' ? 'WEB_PORTAL' : 'MOBILE_APP';
}

export async function markAttendanceCheckIn(
  options?: AttendancePunchOptions,
): Promise<AttendanceActionResult> {
  const body = punchBody(options, defaultSelfServicePunchSource());
  if (isWfhPunchSource(body.attendanceSource)) {
    const blocked = await getWfhCheckInBlockMessage();
    if (blocked) {
      throw Object.assign(new Error(blocked), {
        code: 'WFH_CHECK_IN_NOT_APPROVED',
        message: blocked,
      });
    }
  }
  return attendanceActionResult(
    await submitFaceVerifiedAttendance(API_ENDPOINTS.ATTENDANCE.CHECK_IN, body),
  );
}

export async function markAttendanceCheckOut(
  options?: AttendancePunchOptions,
): Promise<AttendanceActionResult> {
  return attendanceActionResult(
    await submitFaceVerifiedAttendance(
      API_ENDPOINTS.ATTENDANCE.CHECK_OUT,
      punchBody(options, defaultSelfServicePunchSource()),
    ),
  );
}

export async function markAttendanceBreakOut(
  options?: AttendancePunchOptions,
): Promise<AttendanceActionResult> {
  return attendanceActionResult(
    await submitFaceVerifiedAttendance(
      API_ENDPOINTS.ATTENDANCE.BREAK_OUT,
      punchBody(options, defaultSelfServicePunchSource()),
    ),
  );
}

export async function markAttendanceBreakIn(
  options?: AttendancePunchOptions,
): Promise<AttendanceActionResult> {
  return attendanceActionResult(
    await submitFaceVerifiedAttendance(
      API_ENDPOINTS.ATTENDANCE.BREAK_IN,
      punchBody(options, defaultSelfServicePunchSource()),
    ),
  );
}

export async function recordBreakTimesForCheckout(input: {
  attendanceDate: string;
  startClock: string;
  endClock: string;
  recordBreakStart: boolean;
  employeeId?: number;
  attendanceSource?: string;
  faceVerificationLogId?: number;
  kioskId?: number;
  deviceIdentifier?: string;
  livenessSessionId?: number | null;
}): Promise<void> {
  const startClock = input.startClock.trim();
  const endClock = input.endClock.trim();
  // A blank pair means no break was taken. Do not invent break events.
  if (!endClock || (input.recordBreakStart && !startClock)) return;
  const source = input.attendanceSource ?? defaultSelfServicePunchSource();
  const verification = {
    employeeId: input.employeeId,
    faceVerificationLogId: input.faceVerificationLogId,
    kioskId: input.kioskId,
    deviceIdentifier: input.deviceIdentifier,
    livenessSessionId: input.livenessSessionId,
  };
  if (input.recordBreakStart) {
    await markAttendanceBreakOut({
      eventTimestamp: clockOnDateToIso(input.attendanceDate, startClock),
      attendanceSource: source,
      ...verification,
    });
  }
  await markAttendanceBreakIn({
    eventTimestamp: clockOnDateToIso(input.attendanceDate, endClock),
    attendanceSource: source,
    ...verification,
  });
}

function firstNumber(row: ObjectValue, keys: string[]): number | null {
  for (const key of keys) {
    const value = numberOrNull(row[key]);
    if (value !== null) return value;
  }
  return null;
}

function firstText(row: ObjectValue, keys: string[]): string | null {
  for (const key of keys) {
    const value = textOrNull(row[key]);
    if (value) return value;
  }
  return null;
}

function statusFromRow(row: ObjectValue): { statusCode: string | null; statusLabel: string } {
  const statusCode = firstText(row, ['statusCode', 'status']);
  const statusLabel =
    firstText(row, ['statusLabel', 'statusName']) ??
    (statusCode ? statusCode.replace(/_/g, ' ') : 'Status unavailable');
  return { statusCode, statusLabel };
}

function isEmployeeAppliedDashboardRequest(row: {
  employeeName?: string | null;
  reason?: string | null;
  statusCode?: string | null;
}): boolean {
  return (
    isHrDashboardRequestStatus(row.statusCode) &&
    Boolean(row.employeeName?.trim()) &&
    Boolean(row.reason?.trim())
  );
}

/**
 * Employees with no daily-attendance row are missing from Present and from
 * the API Absent count (NULL is_present ≠ 0). When the API reports 0 absent
 * but people are still unaccounted, treat the remainder as absent.
 */
export function reconcileUnaccountedAbsent(
  metrics: AdminAttendanceDashboardMetrics,
): AdminAttendanceDashboardMetrics {
  const total = metrics.totalEmployees;
  const present = metrics.presentToday;
  if (total == null || present == null) return metrics;
  if ((metrics.absent ?? 0) > 0) return metrics;
  const remainder =
    total - present - (metrics.onLeave ?? 0) - (metrics.wfh ?? 0);
  if (remainder <= 0) return metrics;
  return { ...metrics, absent: remainder };
}

/**
 * Maps org attendance dashboard summary fields.
 * Early-exit counts from the API are ignored on purpose — no early-exit UI card.
 */
export function normalizeAdminAttendanceDashboard(
  summary: ObjectValue | null | undefined,
  attendanceDate?: string | null,
): AdminAttendanceDashboardData {
  const row = summary ?? {};
  const metrics: AdminAttendanceDashboardMetrics = {
    totalEmployees: firstNumber(row, [
      'totalEmployees',
      'totalActiveEmployees',
      'totalEmployeeCount',
      'employeeCount',
      'totalStaff',
    ]),
    presentToday: firstNumber(row, [
      'presentToday',
      'presentCount',
      'presentEmployees',
      'present',
      'totalPresent',
    ]),
    absent: firstNumber(row, [
      'absent',
      'absentCount',
      'absentEmployees',
      'totalAbsent',
    ]),
    onLeave: firstNumber(row, [
      'onLeave',
      'onLeaveCount',
      'leaveCount',
      'leaveEmployees',
      'employeesOnLeave',
      'totalOnLeave',
    ]),
    wfh: firstNumber(row, [
      'wfh',
      'wfhCount',
      'workFromHomeCount',
      'wfhEmployees',
      'employeesOnWfh',
      'totalWfh',
    ]),
    lateArrivals: firstNumber(row, [
      'lateArrivals',
      'lateCount',
      'lateEmployees',
      'lateArrivalCount',
      'totalLate',
    ]),
    shortHours: firstNumber(row, [
      'shortHours',
      'shortHoursCount',
      'shortHourCount',
      'shortHoursEmployees',
      'totalShortHours',
    ]),
    // Missing checkout (= did not exit) is a dashboard metric; early exit is not.
    missingCheckout: firstNumber(row, [
      'missingCheckout',
      'missingCheckoutCount',
      'missingCheckOut',
      'missingCheckOutCount',
      'noCheckoutCount',
      'totalMissingCheckout',
    ]),
  };

  return {
    attendanceDate:
      dateOnly(attendanceDate) ??
      dateOnly(row.attendanceDate) ??
      dateOnly(row.reportDate) ??
      null,
    metrics: reconcileUnaccountedAbsent(metrics),
  };
}

export async function getAdminAttendanceDashboard(options?: {
  attendanceDate?: string;
  locationId?: number;
  departmentId?: number;
  scope?: DashboardScope;
}): Promise<AdminAttendanceDashboardData> {
  const isTeam = options?.scope === 'team';
  const params: Record<string, string | number> = {};
  if (options?.attendanceDate?.trim()) {
    params.attendanceDate = validateAttendanceDate(
      options.attendanceDate.trim(),
    );
  }
  if (
    !isTeam &&
    typeof options?.locationId === 'number' &&
    Number.isFinite(options.locationId) &&
    options.locationId > 0
  ) {
    params.locationId = options.locationId;
  }
  if (
    !isTeam &&
    typeof options?.departmentId === 'number' &&
    Number.isFinite(options.departmentId) &&
    options.departmentId > 0
  ) {
    params.departmentId = options.departmentId;
  }

  const response = await apiClient.get<
    Envelope<{ summary?: ObjectValue | null }>
  >(
    isTeam
      ? API_ENDPOINTS.TEAM_DASHBOARD.SUMMARY
      : API_ENDPOINTS.ATTENDANCE.DASHBOARD,
    { params },
  );

  const data = response.data.data ?? {};
  return normalizeAdminAttendanceDashboard(
    data.summary ?? null,
    typeof params.attendanceDate === 'string' ? params.attendanceDate : null,
  );
}

function asObjectRows(value: unknown): ObjectValue[] {
  return Array.isArray(value)
    ? value.filter((row): row is ObjectValue => Boolean(row) && typeof row === 'object')
    : [];
}

function normalizeHrAdminLeave(row: ObjectValue): HrAdminLeaveItem | null {
  const leaveApplicationId = firstNumber(row, [
    'leaveApplicationId',
    'applicationId',
  ]);
  if (leaveApplicationId === null || leaveApplicationId <= 0) return null;
  const status = statusFromRow(row);
  return {
    leaveApplicationId,
    employeeId: firstNumber(row, ['employeeId']),
    employeeName: firstText(row, ['employeeName', 'displayName']),
    employeeNumber: firstText(row, ['employeeNumber']),
    leaveTypeName: firstText(row, ['leaveTypeName', 'leaveName']),
    leaveFromDate: dateOnly(row.leaveFromDate ?? row.fromDate),
    leaveToDate: dateOnly(row.leaveToDate ?? row.toDate),
    totalDays: firstNumber(row, ['totalDays']),
    sessionType: firstText(row, ['sessionType']),
    sessionTypeName: firstText(row, ['sessionTypeName', 'sessionName']),
    reason: firstText(row, ['reason', 'leaveReason']),
    statusCode: status.statusCode,
    statusLabel: status.statusLabel,
    applicationNumber: firstText(row, ['applicationNumber']),
    applicationDate: dateOnly(row.applicationDate),
    workflowInstanceId: firstNumber(row, ['workflowInstanceId']),
    requestTypeCode: firstText(row, ['requestTypeCode', 'entityType']),
  };
}

function normalizeHrAdminWfh(row: ObjectValue): HrAdminWfhItem | null {
  const wfhRequestId = firstNumber(row, ['wfhRequestId']);
  if (wfhRequestId === null || wfhRequestId <= 0) return null;
  const status = statusFromRow(row);
  return {
    wfhRequestId,
    employeeId: firstNumber(row, ['employeeId']),
    employeeName: firstText(row, ['employeeName', 'displayName']),
    employeeNumber: firstText(row, ['employeeNumber']),
    fromDate: dateOnly(row.fromDate ?? row.workFromDate ?? row.wfhFromDate),
    toDate: dateOnly(row.toDate ?? row.workToDate ?? row.wfhToDate),
    totalDays: firstNumber(row, ['totalDays']),
    workLocation: firstText(row, ['workLocation', 'requestType', 'wfhType']),
    reason: firstText(row, ['reason']),
    statusCode: status.statusCode,
    statusLabel: status.statusLabel,
    requestNumber: firstText(row, ['requestNumber']),
    workflowInstanceId: firstNumber(row, ['workflowInstanceId']),
    requestTypeCode: firstText(row, ['requestTypeCode', 'entityType']),
  };
}

function normalizeHrAdminPermission(row: ObjectValue): HrAdminPermissionItem | null {
  const permissionRequestId = firstNumber(row, ['permissionRequestId']);
  if (permissionRequestId === null || permissionRequestId <= 0) return null;
  const status = statusFromRow(row);
  return {
    permissionRequestId,
    employeeId: firstNumber(row, ['employeeId']),
    employeeName: firstText(row, ['employeeName', 'displayName']),
    employeeNumber: firstText(row, ['employeeNumber']),
    permissionDate: dateOnly(row.permissionDate ?? row.requestDate),
    permissionTypeName: firstText(row, [
      'permissionTypeName',
      'permissionType',
    ]),
    startTime: firstText(row, ['startTime', 'fromTime']),
    endTime: firstText(row, ['endTime', 'toTime']),
    durationMinutes: firstNumber(row, ['durationMinutes', 'totalMinutes']),
    reason: firstText(row, ['reason']),
    statusCode: status.statusCode,
    statusLabel: status.statusLabel,
    requestNumber: firstText(row, ['requestNumber']),
    workflowInstanceId: firstNumber(row, ['workflowInstanceId']),
    requestTypeCode: firstText(row, ['requestTypeCode', 'entityType']),
  };
}

function normalizeHrAdminCompensatoryWork(
  row: ObjectValue,
): HrAdminCompensatoryWorkItem | null {
  const compensatoryWorkRequestId = firstNumber(row, [
    'compensatoryWorkRequestId',
  ]);
  if (compensatoryWorkRequestId === null || compensatoryWorkRequestId <= 0) {
    return null;
  }
  const status = statusFromRow(row);
  return {
    compensatoryWorkRequestId,
    employeeId: firstNumber(row, ['employeeId']),
    employeeName: firstText(row, ['employeeName', 'displayName']),
    employeeNumber: firstText(row, ['employeeNumber']),
    holidayDate: dateOnly(row.holidayDate ?? row.workDate),
    reason: firstText(row, ['reason', 'businessReason']),
    statusCode: status.statusCode,
    statusLabel: status.statusLabel,
    checkInAt: firstText(row, ['checkInAt', 'firstCheckInAt']),
    checkOutAt: firstText(row, ['checkOutAt', 'lastCheckOutAt']),
    workedMinutes: firstNumber(row, ['workedMinutes', 'netWorkMinutes']),
    earnedDays: firstNumber(row, ['earnedDays', 'compensatoryLeaveAdded']),
    earnedLeaveTypeName: firstText(row, [
      'earnedLeaveTypeName',
      'leaveTypeName',
    ]),
  };
}

export async function getHrAdminDashboard(options: {
  fromDate: string;
  toDate: string;
  scope?: DashboardScope;
}): Promise<HrAdminDashboardData> {
  const fromDate = validateAttendanceDate(options.fromDate.trim());
  const toDate = validateAttendanceDate(options.toDate.trim());
  if (toDate < fromDate) {
    throw Object.assign(new Error('toDate cannot be earlier than fromDate'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.get<
    Envelope<{
      fromDate?: unknown;
      toDate?: unknown;
      leave?: unknown;
      wfh?: unknown;
      permission?: unknown;
      compensatoryWork?: unknown;
    }>
  >(
    options.scope === 'team'
      ? API_ENDPOINTS.TEAM_DASHBOARD.ACTIVITY
      : API_ENDPOINTS.ATTENDANCE.HR_DASHBOARD,
    { params: { fromDate, toDate } },
  );
  const data = response.data.data ?? {};
  return {
    fromDate: dateOnly(data.fromDate) ?? fromDate,
    toDate: dateOnly(data.toDate) ?? toDate,
    leave: asObjectRows(data.leave)
      .map(normalizeHrAdminLeave)
      .filter((row): row is HrAdminLeaveItem => row !== null)
      .filter(isEmployeeAppliedDashboardRequest),
    wfh: asObjectRows(data.wfh)
      .map(normalizeHrAdminWfh)
      .filter((row): row is HrAdminWfhItem => row !== null)
      .filter(isEmployeeAppliedDashboardRequest),
    permission: asObjectRows(data.permission)
      .map(normalizeHrAdminPermission)
      .filter((row): row is HrAdminPermissionItem => row !== null)
      .filter(isEmployeeAppliedDashboardRequest),
    compensatoryWork: asObjectRows(data.compensatoryWork)
      .map(normalizeHrAdminCompensatoryWork)
      .filter((row): row is HrAdminCompensatoryWorkItem => row !== null)
      .filter(isEmployeeAppliedDashboardRequest),
  };
}

// Kept for callers that already hold a details payload with events.
export function getAttendanceEventsFromDetails(
  details: AttendanceDetails,
): AttendanceEvent[] {
  return sortEvents(details.events);
}

function emptySummary(): WeeklyAttendanceSummary {
  return {
    totalWorkingDays: 0,
    presentDays: 0,
    daysWorked: 0,
    absentDays: 0,
    lateDays: 0,
    shortHoursDays: 0,
    totalWorkedMinutes: 0,
    totalExpectedMinutes: 0,
    totalLateMinutes: 0,
    totalShortHoursMinutes: 0,
  };
}

function elapsedMinutesBetween(
  arrival: string | null,
  exit: string | null,
): number | null {
  if (!arrival || !exit) return null;
  const clockMinutes = (value: string): number | null => {
    const match = value.match(
      /(?:^|[T\s])(\d{1,2}):(\d{2})(?::\d{2})?(?:$|[Z+.-])/,
    );
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    return hours <= 23 && minutes <= 59 ? hours * 60 + minutes : null;
  };
  const start = clockMinutes(arrival);
  const end = clockMinutes(exit);
  if (start === null || end === null) return null;
  return end >= start ? end - start : end + 24 * 60 - start;
}

function normalizeWeeklyDay(row: ObjectValue): WeeklyAttendanceDay | null {
  const date = dateOnly(row.date ?? row.attendanceDate);
  if (!date) return null;
  const arrivalTime = textOrNull(row.arrivalTime ?? row.firstCheckInAt);
  const exitTime = textOrNull(row.exitTime ?? row.lastCheckOutAt);
  const reportedWorkedMinutes = numberOrNull(
    row.netWorkMinutes ?? row.workedMinutes ?? row.net_work_minutes,
  );
  const reportedTotalMinutes =
    numberOrNull(
      row.totalWorkMinutes ??
        row.total_work_minutes ??
        row.grossWorkMinutes ??
        row.gross_work_minutes ??
        row.totalMinutes ??
        row.durationMinutes,
    ) ?? elapsedMinutesBetween(arrivalTime, exitTime);
  const reportedLunchBreakMinutes = numberOrNull(
    row.lunchBreakMinutes ?? row.breakMinutes ?? row.lunch_break_minutes,
  );
  const lunchBreakMinutes =
    reportedLunchBreakMinutes ??
    (reportedTotalMinutes !== null && reportedWorkedMinutes !== null
      ? Math.max(0, reportedTotalMinutes - reportedWorkedMinutes)
      : 0);
  const totalMinutes =
    reportedTotalMinutes ??
    (reportedWorkedMinutes !== null
      ? reportedWorkedMinutes + lunchBreakMinutes
      : null);
  const nestedShift =
    row.shift && typeof row.shift === 'object'
      ? (row.shift as ObjectValue)
      : null;
  const nestedPolicy =
    row.workPolicy && typeof row.workPolicy === 'object'
      ? (row.workPolicy as ObjectValue)
      : row.policy && typeof row.policy === 'object'
        ? (row.policy as ObjectValue)
        : null;
  const expectedWorkMinutes =
    numberOrNull(
      row.expectedWorkMinutes ??
        row.expected_work_minutes ??
        nestedShift?.expectedWorkMinutes ??
        nestedShift?.expected_work_minutes ??
        nestedPolicy?.expectedWorkMinutes ??
        nestedPolicy?.expected_work_minutes,
    ) ?? 0;
  const policyLunchBreakMinutes = numberOrNull(
    row.policyLunchBreakMinutes ??
      row.policy_lunch_break_minutes ??
      row.configuredLunchBreakMinutes ??
      nestedShift?.lunchBreakMinutes ??
      nestedShift?.lunch_break_minutes ??
      nestedPolicy?.lunchBreakMinutes ??
      nestedPolicy?.lunch_break_minutes,
  );
  const spreadMinutes = resolveAttendanceSpreadMinutes({
    spreadMinutes: numberOrNull(
      row.spreadMinutes ??
        row.spread_minutes ??
        row.spreadDuration ??
        nestedShift?.spreadMinutes ??
        nestedShift?.spread_minutes ??
        nestedPolicy?.spreadMinutes ??
        nestedPolicy?.spread_minutes,
    ),
    expectedWorkMinutes,
    policyLunchBreakMinutes,
    actualBreakMinutes: lunchBreakMinutes,
  });
  return applyWorkHoursCoverageToWeeklyDay({
    date,
    arrivalTime,
    exitTime,
    totalMinutes,
    lunchBreakMinutes,
    workedMinutes: reportedWorkedMinutes,
    expectedWorkMinutes,
    spreadMinutes,
    policyLunchBreakMinutes,
    lateMinutes: numberOrNull(row.lateMinutes) ?? 0,
    shortHoursMinutes: numberOrNull(row.shortHoursMinutes) ?? 0,
    statusCode: textOrNull(row.statusCode) ?? 'ABSENT',
    statusLabel: textOrNull(row.statusLabel) ?? 'Absent',
    dayStatus: parseAttendanceDayStatus(row.dayStatus),
    isLate: boolOrNull(row.isLate) ?? undefined,
    isShortHours: boolOrNull(row.isShortHours) ?? undefined,
    isPresent: boolOrNull(row.isPresent) ?? undefined,
    isHalfDay: boolOrNull(row.isHalfDay) ?? undefined,
    isHalfDayLeave: boolOrNull(row.isHalfDayLeave) ?? undefined,
    isOnLeave: boolOrNull(row.isOnLeave) ?? undefined,
    isWfh: boolOrNull(row.isWfh) ?? undefined,
    missingCheckOut: boolOrNull(row.missingCheckOut) ?? undefined,
    isHoliday: boolOrNull(row.isHoliday) ?? undefined,
    isWeeklyOff: boolOrNull(row.isWeeklyOff) ?? undefined,
    isManuallyAdjusted: boolOrNull(row.isManuallyAdjusted) ?? undefined,
    attendanceMode: textOrNull(row.attendanceMode),
    checkInSource: textOrNull(row.checkInSource),
    checkOutSource: textOrNull(row.checkOutSource),
    checkInOriginalSource: textOrNull(row.checkInOriginalSource),
    checkOutOriginalSource: textOrNull(row.checkOutOriginalSource),
    checkInKioskId: numberOrNull(row.checkInKioskId ?? row.check_in_kiosk_id),
    checkOutKioskId: numberOrNull(row.checkOutKioskId ?? row.check_out_kiosk_id),
    checkInFaceVerified: boolOrNull(row.checkInFaceVerified),
    checkOutFaceVerified: boolOrNull(row.checkOutFaceVerified),
    checkInDeviceIdentifier: firstText(row, [
      'checkInDeviceIdentifier',
      'check_in_device_identifier',
    ]),
    checkOutDeviceIdentifier: firstText(row, [
      'checkOutDeviceIdentifier',
      'check_out_device_identifier',
    ]),
    checkInKioskName: firstText(row, [
      'checkInKioskName',
      'check_in_kiosk_name',
      'firstCheckInKioskName',
    ]),
    checkOutKioskName: firstText(row, [
      'checkOutKioskName',
      'check_out_kiosk_name',
      'lastCheckOutKioskName',
    ]),
    checkInLocationName: textOrNull(row.checkInLocationName),
    checkOutLocationName: textOrNull(row.checkOutLocationName),
    leaveTypeName: textOrNull(row.leaveTypeName),
    leaveSessionName: textOrNull(row.leaveSessionName),
    leaveSessionType: textOrNull(row.leaveSessionType),
    leaveDays: numberOrNull(row.leaveDays),
    permissionMinutes: numberOrNull(row.permissionMinutes),
    permissionTypeName: textOrNull(row.permissionTypeName),
    permissionStartTime: textOrNull(row.permissionStartTime),
    permissionEndTime: textOrNull(row.permissionEndTime),
    permissionUsed:
      boolOrNull(row.permissionUsed) ??
      ((numberOrNull(row.permissionMinutes) ?? 0) > 0),
  });
}

function normalizeWeeklySummary(
  row: ObjectValue | null | undefined,
): WeeklyAttendanceSummary {
  if (!row) return emptySummary();
  return {
    totalWorkingDays: numberOrNull(row.totalWorkingDays) ?? 0,
    presentDays: numberOrNull(row.presentDays) ?? 0,
    daysWorked:
      numberOrNull(row.daysWorked) ?? numberOrNull(row.presentDays) ?? 0,
    absentDays: numberOrNull(row.absentDays) ?? 0,
    lateDays: numberOrNull(row.lateDays) ?? 0,
    shortHoursDays: numberOrNull(row.shortHoursDays) ?? 0,
    totalWorkedMinutes: numberOrNull(row.totalWorkedMinutes) ?? 0,
    totalExpectedMinutes: numberOrNull(row.totalExpectedMinutes) ?? 0,
    totalLateMinutes: numberOrNull(row.totalLateMinutes) ?? 0,
    totalShortHoursMinutes: numberOrNull(row.totalShortHoursMinutes) ?? 0,
    totalLeaveDays: numberOrNull(row.totalLeaveDays) ?? 0,
    totalLopDays: numberOrNull(row.totalLopDays) ?? 0,
  };
}

function normalizeWeeklyEmployee(
  row: ObjectValue,
): WeeklyAttendanceEmployeeReport | null {
  const employeeId = numberOrNull(row.employeeId);
  if (!employeeId) return null;
  const weekStart = dateOnly(row.weekStart) ?? '';
  const weekEnd = dateOnly(row.weekEnd) ?? '';
  const days = Array.isArray(row.days)
    ? (row.days as ObjectValue[])
        .map(normalizeWeeklyDay)
        .filter((item): item is WeeklyAttendanceDay => item !== null)
    : [];
  const summary = normalizeWeeklySummary(row.summary as ObjectValue | undefined);
  return {
    employeeId,
    employeeName: textOrNull(row.employeeName ?? row.displayName) ?? 'Employee',
    employeeEmail: textOrNull(row.employeeEmail ?? row.officialEmail),
    employeeNumber: textOrNull(row.employeeNumber),
    weekStart,
    weekEnd,
    days,
    summary: {
      ...summary,
      shortHoursDays: days.filter(day => day.shortHoursMinutes > 0).length,
      totalShortHoursMinutes: days.reduce(
        (total, day) => total + day.shortHoursMinutes,
        0,
      ),
    },
  };
}

function normalizeWeeklyResult(
  data: ObjectValue | null | undefined,
  fallbackWeek: { weekStart: string; weekEnd: string },
): WeeklyAttendanceResult {
  const items = Array.isArray(data?.items)
    ? (data.items as ObjectValue[])
        .map(normalizeWeeklyEmployee)
        .filter((item): item is WeeklyAttendanceEmployeeReport => item !== null)
    : [];
  const pagination = (data?.pagination as ObjectValue | undefined) ?? {};
  const page = numberOrNull(pagination.page) ?? 1;
  const pageSize = numberOrNull(pagination.pageSize) ?? 20;
  const totalRecords = numberOrNull(pagination.totalRecords) ?? items.length;
  return {
    weekStart: dateOnly(data?.weekStart) ?? fallbackWeek.weekStart,
    weekEnd: dateOnly(data?.weekEnd) ?? fallbackWeek.weekEnd,
    items,
    pagination: {
      page,
      pageSize,
      totalRecords,
      totalPages:
        numberOrNull(pagination.totalPages) ??
        (pageSize > 0 ? Math.ceil(totalRecords / pageSize) : 0),
    },
  };
}

export async function getMyWeeklyAttendance(options: {
  weekStart: string;
  weekEnd: string;
}): Promise<WeeklyAttendanceResult> {
  const weekStart = validateAttendanceDate(options.weekStart);
  const weekEnd = validateAttendanceDate(options.weekEnd);
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.ATTENDANCE.WEEKLY_SUMMARY,
    { params: { weekStart, weekEnd } },
  );
  return normalizeWeeklyResult(response.data.data, { weekStart, weekEnd });
}

export async function getEmployeeOverallWeeklyReport(options: {
  weekStart: string;
  weekEnd: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<WeeklyAttendanceResult> {
  const weekStart = validateAttendanceDate(options.weekStart);
  const weekEnd = validateAttendanceDate(options.weekEnd);
  const params: Record<string, string | number> = { weekStart, weekEnd };
  if (options.search?.trim()) params.search = options.search.trim();
  if (typeof options.page === 'number') params.page = options.page;
  if (typeof options.pageSize === 'number') params.pageSize = options.pageSize;

  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.ATTENDANCE.EMPLOYEE_OVERALL_WEEKLY_REPORT,
    { params },
  );
  return normalizeWeeklyResult(response.data.data, { weekStart, weekEnd });
}

function normalizeMonthlyDay(row: ObjectValue): EmployeeMonthlyAttendanceDay | null {
  const date = dateOnly(row.date);
  if (!date) return null;
  return {
    date,
    code: textOrNull(row.code) ?? '-',
    statusCode: textOrNull(row.statusCode),
    statusLabel: textOrNull(row.statusLabel) ?? '-',
    notes: Array.isArray(row.notes)
      ? row.notes.map(textOrNull).filter((note): note is string => note !== null)
      : [],
  };
}

function normalizeMonthlyRow(row: ObjectValue): EmployeeMonthlyAttendanceRow | null {
  const employeeId = numberOrNull(row.employeeId);
  if (!employeeId) return null;
  const totals = (row.totals as ObjectValue | undefined) ?? {};
  const total = (key: string) => numberOrNull(totals[key]) ?? 0;
  return {
    employeeId,
    employeeName: textOrNull(row.employeeName) ?? 'Employee',
    employeeNumber: textOrNull(row.employeeNumber),
    employeeEmail: textOrNull(row.employeeEmail),
    days: Array.isArray(row.days)
      ? (row.days as ObjectValue[])
          .map(normalizeMonthlyDay)
          .filter((day): day is EmployeeMonthlyAttendanceDay => day !== null)
      : [],
    totals: {
      presentDays: total('presentDays'),
      absentDays: total('absentDays'),
      leaveDays: total('leaveDays'),
      lopDays: total('lopDays'),
      halfDays: total('halfDays'),
      holidays: total('holidays'),
      weeklyOffs: total('weeklyOffs'),
    },
  };
}

/**
 * Organization-wide month grid: one row per employee, one abbreviated status
 * per date (1st to last day), with month totals calculated by the backend.
 */
export async function getEmployeeMonthlyAttendanceReport(options: {
  year: number;
  month: number;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<EmployeeMonthlyAttendanceResult> {
  if (
    !Number.isInteger(options.year) ||
    !Number.isInteger(options.month) ||
    options.month < 1 ||
    options.month > 12
  ) {
    throw new Error('Select a valid month and year.');
  }
  const params: Record<string, string | number> = {
    year: options.year,
    month: options.month,
  };
  if (options.search?.trim()) params.search = options.search.trim();
  if (typeof options.page === 'number') params.page = options.page;
  if (typeof options.pageSize === 'number') params.pageSize = options.pageSize;

  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.ATTENDANCE.EMPLOYEE_MONTHLY_ATTENDANCE_REPORT,
    { params },
  );
  const data = (response.data.data ?? {}) as ObjectValue;
  const items = Array.isArray(data.items)
    ? (data.items as ObjectValue[])
        .map(normalizeMonthlyRow)
        .filter((item): item is EmployeeMonthlyAttendanceRow => item !== null)
    : [];
  const legend = Array.isArray(data.legend)
    ? (data.legend as ObjectValue[])
        .map((entry): EmployeeMonthlyAttendanceLegendEntry | null => {
          const code = textOrNull(entry.code);
          const label = textOrNull(entry.label);
          if (!code || !label) return null;
          return {
            code,
            label,
            kind: entry.kind === 'leave' ? 'leave' : 'status',
            statusLabel: textOrNull(entry.statusLabel) ?? undefined,
          };
        })
        .filter((entry): entry is EmployeeMonthlyAttendanceLegendEntry => entry !== null)
    : [];
  const pagination = (data.pagination as ObjectValue | undefined) ?? {};
  const page = numberOrNull(pagination.page) ?? 1;
  const pageSize = numberOrNull(pagination.pageSize) ?? 20;
  const totalRecords = numberOrNull(pagination.totalRecords) ?? items.length;
  return {
    year: numberOrNull(data.year) ?? options.year,
    month: numberOrNull(data.month) ?? options.month,
    monthStart: dateOnly(data.monthStart) ?? '',
    monthEnd: dateOnly(data.monthEnd) ?? '',
    dates: Array.isArray(data.dates)
      ? data.dates.map(dateOnly).filter((date): date is string => date !== null)
      : [],
    legend,
    items,
    pagination: {
      page,
      pageSize,
      totalRecords,
      totalPages:
        numberOrNull(pagination.totalPages) ??
        (pageSize > 0 ? Math.ceil(totalRecords / pageSize) : 0),
    },
  };
}

export async function getWeeklyAttendanceReports(options: {
  weekStart: string;
  weekEnd: string;
  search?: string;
  employeeId?: number;
  page?: number;
  pageSize?: number;
}): Promise<WeeklyAttendanceResult> {
  const weekStart = validateAttendanceDate(options.weekStart);
  const weekEnd = validateAttendanceDate(options.weekEnd);
  const params: Record<string, string | number> = { weekStart, weekEnd };
  if (options.search?.trim()) params.search = options.search.trim();
  if (
    typeof options.employeeId === 'number' &&
    Number.isInteger(options.employeeId) &&
    options.employeeId > 0
  ) {
    params.employeeId = options.employeeId;
  }
  if (typeof options.page === 'number') params.page = options.page;
  if (typeof options.pageSize === 'number') params.pageSize = options.pageSize;

  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.ATTENDANCE.WEEKLY_REPORTS,
    { params },
  );
  return normalizeWeeklyResult(response.data.data, { weekStart, weekEnd });
}

export { eventLabel as mapAttendanceEventLabel };
